import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Newspaper, Settings, UserRound, UsersRound } from 'lucide-react';
import { useLayoutEffect, useRef, type ComponentType, type MouseEvent, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

import { QlashIcon } from '@/components/common/QlashIcon';
import { useDmNonLetti } from '@/hooks/useDmNonLetti';
import { useAppSelector } from '@/store/hooks';
import { selectUnreadInboxCount } from '@/store/engagement.slice';
import { BOUNCE, useSlidingIndicator } from '@/hooks/useSlidingIndicator';
import { cn } from '@/lib/cn';
import { useChiediPrimaDiUscire } from '@/contexts/UscitaTavoloContext';
import { registraIngombroBasso } from '@/lib/ingombroBasso';
import { ALTEZZA_TABBAR_VAR, normalizzaPercorso, voceAccesa, type VoceBarra } from './tabBarRoutes';

interface TabDef {
  to: string;
  key: VoceBarra;
  /** Il nome accessibile della voce, nel namespace `general`: chiavi che esistono gia' nelle quattro lingue. */
  labelKey: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
}

// 5 voci, solo icone: News · Amici · [Hub] · Impostazioni · Profilo. La Hub resta al centro (3ª).
// Il nome accessibile e' `labelKey`, tradotto (prima era la chiave grezza: un lettore di schermo diceva «inbox»).
//
// 🔴 I-318, Luca 2026-10-03 ~12:5xZ, testuale: «Nella menu bottom bar, l'icona hub (logo qlash) differisce troppo
// dagli altri bottoni. Rendilo uguale stile agli altri, ingrandendo se necessario logo qlash. Al posto dell'icona
// inbox, mettiamo icona settings per accedere più comodamente ai settings dell'utente.» Quindi:
// - la Hub e' una voce COME LE ALTRE: stesso colore spento e acceso, nessuna pallina sua, e la pastiglia che scorre
//   vale anche per lei. Il logo e' piu' grande delle altre icone solo per pareggiarne l'inchiostro (`HUB_LOGO`);
// - la quarta voce e' l'INGRANAGGIO verso /settings, al posto della campanella dell'Inbox. L'Inbox resta una rotta
//   (Impostazioni → Comunicazione → Inbox) e il pallino dei suoi non letti passa sull'ingranaggio (decisione di
//   flotta, 2026-10-03 12:58:10Z): stesso selettore della riga «Inbox» in Comunicazione, nessun secondo conteggio.
//
// L'ultima voce resta il PROFILO, non le impostazioni: la schermata dice chi sei e cosa scegli. Alle impostazioni
// ora si arriva da due porte: questa scheda e la tasca dello Zaino sul Profilo (la lista e' la stessa, `SettingsList`).
const tabs: readonly TabDef[] = [
  { to: '/news', key: 'news', labelKey: 'entities.news', Icon: Newspaper },
  { to: '/friends', key: 'friends', labelKey: 'friends', Icon: UsersRound },
  { to: '/', key: 'hub', labelKey: 'hub', Icon: HubLogo },
  { to: '/settings', key: 'settings', labelKey: 'settings', Icon: Settings },
  { to: '/profile', key: 'profile', labelKey: 'profile', Icon: UserRound },
] as const;

/**
 * Il logo QLASH alla PARITA' OTTICA con le icone lucide della barra (I-318).
 *
 * Il glifo occupa solo una parte del suo quadrato (viewBox 40×40): a `h-6 w-6` come le altre icone l'inchiostro
 * era 12,4×8,3 px contro 18-22 × 20-22 delle icone a tratto [M misura-tabbar.mjs, build:stg, 2026-10-03] — meno
 * di due terzi della larghezza, ed e' il «differisce troppo» della richiesta. La taglia qui porta la LARGHEZZA
 * dell'inchiostro a quella delle icone vicine piu' larghe (Notizie, Amici: 22 px): riquadro 44 ⇒ inchiostro ≈ 22,8×15,3.
 * Scelta fra 40 / 44 / 48 guardate sulla stessa barra (tavola nel KB): a 40 (larghezza = la MEDIA delle vicine, 20,5)
 * il glifo, basso e scavato, si leggeva ancora piu' piccolo; a 48 (≈ 25 px) dominava la riga.
 * Non a pari AREA (riquadro ~49): un glifo pieno pesa piu' di un'icona a tratto e si leggerebbe piu' grande.
 * Numeri e tavole nel KB: services/qlash-telegram-app/ui/2026-10-03-tabbar-hub-impostazioni.md.
 * ⚠️ Il margine NEGATIVO (-10 px per lato) tiene il posto in pagina a 24×24, come le altre icone: la pastiglia resta
 * 64×40 su tutte le voci e la barra non cresce. Il centro dell'inchiostro coincide col centro della voce (±0,1 px).
 * ⚠️ Se un giorno cambia la misura delle altre icone (`h-6 w-6`), va rifatta anche questa: la parita' e' la
 * richiesta, non il numero.
 */
const HUB_LOGO = 'h-11 w-11 -m-2.5';
function HubLogo(props: SVGProps<SVGSVGElement>) {
  return <QlashIcon className={cn(props.className, HUB_LOGO)} />;
}

export function TabBar() {
  const { t } = useTranslation('general');
  const navRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const root = document.documentElement;
    // L'ingombro in basso: la barra REGISTRA la sua altezza (come `TabBarV3`) e la variabile e` il massimo dei contributi, v. `lib/ingombroBasso.ts`; da sola vale quanto la barra, come prima.
    const contributo = registraIngombroBasso(() => el.getBoundingClientRect().height);
    const pubblica = () => {
      root.style.setProperty(ALTEZZA_TABBAR_VAR, `${el.getBoundingClientRect().height}px`);
      contributo.aggiorna();
    };
    pubblica();
    // L'inset di sicurezza arriva piu' tardi del primo disegno (l'SDK di Telegram lo scrive dopo) e cambia girando il
    // telefono: la misura si rifa' quando la barra cambia misura, non solo al montaggio.
    const osservatore = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(pubblica);
    osservatore?.observe(el);
    return () => {
      osservatore?.disconnect();
      root.style.removeProperty(ALTEZZA_TABBAR_VAR);
      contributo.rimuovi();
    };
  }, []);
  const unreadInbox = useAppSelector(selectUnreadInboxCount);
  // D1: messaggi diretti non letti ⇒ il pallino su Amici, dove sta «Messaggi» (L-2238 A3 (1)). La fonte e'
  // `/social/dm/unread` (0 a flag spento: l'hook non chiede niente).
  const dmNonLetti = useDmNonLetti();
  const { pathname } = useLocation();
  /**
   * Quale voce e' accesa lo decide `voceAccesa` (`tabBarRoutes.ts`), per prefisso: dal 2026-10-04 (I-382) la barra
   * c'e' anche nelle sotto-schermate e una voce e' SEMPRE accesa (la Hub per le sezioni di gioco e i tornei). Non
   * `NavLink`/`isActive`: la sua regola (la Hub con `end`) non e' questa, e due definizioni di «dove sei» divergono.
   */
  const active = voceAccesa(pathname);

  /**
   * 🔴 LA CONFERMA PRIMA DI LASCIARE UN TAVOLO VIVO (I-392, Luca «4 (b)»). Dal tavolo di una partita di BACKGAMMON, con il MIO
   * orologio che corre in una partita con orologio, un tocco su una voce non naviga subito: apre «Lasciare il tavolo?», e solo
   * «Esci» porta alla voce toccata. In ogni altro caso la barra e' quella di sempre. Il quando, il foglio e il suo stato stanno
   * in `contexts/UscitaTavoloContext.tsx` (UNO per la barra e per il tasto indietro di Telegram, I-402): qui si chiede solo
   * «lo chiedo prima?» passando il passo. Fuori dal provider (i banchi) la risposta e' sempre «no».
   */
  const navigate = useNavigate();
  const chiediPrimaDiUscire = useChiediPrimaDiUscire();
  const alTocco = (e: MouseEvent<HTMLAnchorElement>, to: string) => {
    if (chiediPrimaDiUscire(() => navigate(to))) e.preventDefault();
  };
  /** Sulla scheda stessa il link porta `aria-current="page"`; da una sotto-schermata della sua parte, `true`. */
  const eSchedaEsatta = (to: string) => normalizzaPercorso(pathname) === to;

  /**
   * La pastiglia che insegue la voce attiva — stesso meccanismo del segmentato
   * e delle schede sottolineate, quindi lo stesso rimbalzo. Un movimento
   * diverso qui direbbe che è un'altra cosa, e non lo è.
   *
   * Vale anche sulla Hub, dal 2026-10-03 (I-318): prima la Hub aveva una pallina
   * piena sua e qui la pastiglia si spegneva, per non dare due indicatori allo
   * stesso stato. Ora la pallina non c'e' piu' e l'indicatore e' uno solo per
   * tutte e cinque le voci.
   */
  const { containerRef, rect, ready } = useSlidingIndicator(active);
  /**
   * Sul Profilo la barra è la cinghia dello zaino: cuoio invece che teal.
   * 🔴 Sul percorso ESATTO `/profile`, non sulla voce: `/profile/:id` (il profilo di un altro giocatore) ha la voce
   * Profilo accesa ma non e' lo zaino, ne' il cuoio — la barra diventerebbe marrone sotto una schermata che non lo e'.
   */
  const onPack = normalizzaPercorso(pathname) === '/profile';

  return (
    <nav
      ref={navRef}
      className={cn(
        // Angoli superiori arrotondati: la barra smette di essere un bordo
        // dello schermo e diventa una superficie appoggiata sopra il contenuto,
        // in famiglia con le card. Solo in alto — sotto tocca il bordo del
        // dispositivo, e arrotondare li' lascerebbe passare due spicchi di
        // sfondo negli angoli.
        'sticky bottom-0 z-10 rounded-t-2xl',
        // OPACA, non `bg-bg/95` con `backdrop-blur`.
        // Il velo sfocato prendeva la texture a scaglie che sta su <body> con
        // `background-attachment: fixed` e ne stampava una copia SFOCATA dentro
        // la barra — mentre appena fuori, negli spicchi lasciati liberi dagli
        // angoli, la stessa texture restava NITIDA. Due versioni dello stesso
        // motivo a un pixel di distanza: e' quello che si leggeva come uno
        // stacco sbagliato, non l'arrotondamento.
        // Il 95% inoltre rendeva la barra un filo diversa da ogni altra
        // superficie dell'app, che sono tutte piene.
        // ⚠️ UNA sola espressione, non due classi che si accavallano. Prima
        // c'erano `onPack && 'bg-pack'` piu' avanti e `'bg-bg'` qui: entrambe
        // finivano nella lista, e a parita' di proprieta' vince l'ULTIMA — la
        // barra restava verde acqua sul Profilo mentre il codice diceva marrone.
        //
        // Sul Profilo la barra e' la cinghia dello zaino: una cinghia attaccata
        // a una superficie verde acqua sarebbe appiccicata a un'interfaccia.
        onPack ? 'bg-pack' : 'bg-bg',
        // Bordo CONTINUO attorno alla forma, non `border-t`.
        // Un bordo solo superiore segue la curva degli angoli e poi si
        // interrompe: restano due tratti di fianco senza segno, e la riga in
        // alto sembra avere due buchi alle estremita'. L'anello interno di
        // `card-edge` gira tutt'intorno, quindi la barra ha un contorno solo —
        // ed e' lo stesso segno di ogni altra card (ADR-0038, punto 2).
        // Sul cuoio il contorno e' la CUCITURA, non il filo teal delle card.
        onPack ? '[box-shadow:inset_0_0_0_1px_var(--color-pack-stitch)]' : 'card-edge',
        // Inset di sicurezza PIENO + 8px, e l'inset lo assorbe QUI dentro: così
        // il fondo della barra arriva al bordo fisico dello schermo (niente
        // striscia di texture sotto) mentre le icone restano sopra la barra
        // gesti, dove i tocchi non competono con quelli di sistema.
        //
        // Il MAGGIORE fra le due fonti, non una sola: `env()` è lo standard del
        // browser e `--tg-viewport-safe-area-inset-bottom` è quello che dichiara
        // Telegram, e non sempre coincidono — dentro la WebView capita che uno
        // dei due valga zero. Prendendo il massimo la barra è corretta comunque,
        // e su desktop, dove valgono zero entrambi, resta il solo margine fisso.
        //
        // 🔴 QUEL MARGINE FISSO È 4px, NON 8, ED È DA LÌ CHE ESCONO I PIXEL DI
        // «ABBASSA BENE» (scelta di Luca, 2026-09-07: 48 px). Misurato: la barra
        // passa da 52 a 48 e il bersaglio di tocco per voce resta **44 px**, cioè
        // il minimo raccomandato (Apple HIG 44 pt).
        // ⛔ I pixel si prendono DA QUI e non dal `min-h-11` della voce: abbassare
        // la voce a 37 px darebbe gli stessi 45-48 px di barra e -7 di bersaglio,
        // e in più costringerebbe a rimpicciolire la pallina della Hub (il cerchio
        // da 40 non entra in una voce da 37) — cioè a disfare la parità del logo.
        // Misurato: voce 37 ⇒ barra 48 comunque, bersaglio 40; per arrivare
        // davvero a 45 servirebbe pastiglia 24 e cerchio 32, bersaglio 37.
        // ⚠️ Questo è respiro AGGIUNTIVO sopra l'inset di sicurezza, non l'inset:
        // su un telefono con la barra dei gesti l'inset (~34px) domina e la
        // differenza non si vede. Si vede solo dove l'inset è zero.
        'pb-[calc(max(env(safe-area-inset-bottom,0px),var(--tg-viewport-safe-area-inset-bottom,0px))+0.25rem)]',
      )}
    >
      {/* Il `flex` scende su questo contenitore invece di stare sul <nav>: la
          pastiglia si posiziona in assoluto rispetto a lui, e serve un elemento
          `relative` che NON sia la barra — sulla barra il posizionamento
          partirebbe da dentro il bordo e l'inset di sicurezza in fondo. */}
      <div ref={containerRef} className="relative flex">
        {/* Sotto le icone (`-z-10`), non dietro un fondo: la pastiglia passa
            SOTTO le icone quando le attraversa, altrimenti per un istante le copre.
            `opacity` e non smontaggio: senza voce attiva (la sezione tornei)
            sparisce ma resta dov'era, così tornando su una voce riparte da lì
            invece di comparire di colpo a destinazione. */}
        {rect && (
          <span
            aria-hidden
            className={cn(
              'absolute -z-10 rounded-full',
              // Sul cuoio non è una pastiglia PIENA ma una CUCITURA: contorno
              // tratteggiato, niente fondo. Su un oggetto di pelle il modo di
              // marcare una zona non è colorarla, è cucirci attorno — e il
              // tratteggio è il punto del filo.
              // Funziona anche perché il vuoto lascia vedere il cuoio: una
              // pastiglia piena avrebbe coperto proprio il materiale che
              // dà identità a questa schermata.
              onPack ? 'border-2 border-dashed border-pack-fg/55 bg-transparent' : 'bg-brand/15',
            )}
            style={{
              left: rect.left,
              top: rect.top,
              width: rect.width,
              height: rect.height,
              // Spenta quando nessuna voce e' attiva (la sezione tornei, #473): la posizione resta
              // quella dell'ultima voce, e accesa direbbe «sei qui» su News o Impostazioni.
              // ⚠️ Sulla Hub NON si spegne piu' (I-318): la Hub e' una voce come le altre.
              opacity: active === undefined ? 0 : 1,
              // `ready` sopprime la transizione al primo disegno: senza,
              // all'avvio la pastiglia partirebbe dall'angolo e volerebbe fino
              // alla voce attiva, raccontando un cambio mai avvenuto.
              transition: ready
                ? `left 320ms ${BOUNCE}, width 320ms ${BOUNCE}, opacity 160ms ease-out`
                : 'none',
            }}
          />
        )}

        {tabs.map(({ to, key, labelKey, Icon }) => {
          const isActive = active === key;
          return (
          <Link
            key={to}
            data-tour={`tab-${key}`}
            to={to}
            aria-label={t(labelKey)}
            onClick={(e) => alTocco(e, to)}
            aria-current={isActive ? (eSchedaEsatta(to) ? 'page' : 'true') : undefined}
            className={
              cn(
                // ALTEZZA DICHIARATA, non ricavata da un padding.
                // Prima era `py-2` sopra una pastiglia di 36 px: 52 px di link,
                // 60 di barra. Ora l'altezza del bersaglio è scritta —
                // `min-h-11` = 44 px — e il contenuto ci si centra dentro.
                // 🔴 44 px NON è un numero scelto per stare stretti: è il minimo
                // raccomandato per un bersaglio di tocco (Apple HIG 44 pt), ed è
                // il PAVIMENTO di questa richiesta. «Abbassare bene» oltre questa
                // riga non si vede in uno screenshot e si sente sul telefono di
                // chi ha le dita grosse: sotto i 44 px si sbaglia voce.
                // ⚠️ La barra a schermo è 44 + 8 px di respiro in fondo, e sul
                // telefono ci si somma l'inset di sicurezza (34 px su un iPhone
                // col gesto): il numero locale non è quello che vede l'utente.
                'flex min-h-11 flex-1 flex-col items-center justify-center transition-colors',
                // Sul cuoio le icone spente prendono il tono chiaro dello zaino:
                // il teal spento su marrone si legge sporco, per la stessa
                // ragione per cui il grigio si legge sporco sul teal.
                // Sul cuoio anche l'ATTIVA non è teal: dentro lo zaino l'accento
                // dell'app non ha voce. La voce attiva si distingue perché è
                // chiara e ha la pastiglia sotto, non perché è di un altro colore.
                isActive
                  ? onPack
                    ? 'text-pack-fg'
                    : 'text-brand'
                  : onPack
                    ? 'text-pack-muted'
                    : 'text-fg-muted',
              )
            }
          >
            {
              // Lo span ESTERNO è quello che la pastiglia misura, e il suo
              // padding è la forma della pastiglia: allargando qui si allarga
              // lei. Misurare direttamente l'icona darebbe un cerchietto di
              // 24px, misurare il link darebbe una fascia larga un quinto della
              // barra — la forma giusta non è nessuna delle due.
              // `data-active` vale anche sulla Hub (I-318): la pastiglia ci va sotto
              // come sotto ogni altra voce.
              <span
                data-active={isActive ? 'true' : undefined}
                // `py-2`: 24 + 16 = 40 px di pastiglia dentro una voce di 44. Il
                // logo della Hub è più grande (`HUB_LOGO`) ma tiene il posto di
                // 24×24, quindi la pastiglia è la stessa su tutte e cinque.
                className="flex items-center justify-center rounded-full px-5 py-2"
              >
                <span className="relative">
                  <Icon aria-hidden className="h-6 w-6" />
                  {/* I pallini dei non letti: Amici per i messaggi diretti (D1), e
                      l'ingranaggio per l'Inbox, che ora sta dentro le Impostazioni
                      (Impostazioni → Comunicazione → Inbox; flotta, 2026-10-03
                      12:58:10Z). Stesso selettore della riga «Inbox» di
                      `settings/Communication.tsx`: un conteggio solo. */}
                  {((key === 'settings' && unreadInbox > 0) || (key === 'friends' && dmNonLetti > 0)) && (
                    <span
                      aria-hidden
                      data-testid={`tab-pallino-${key}`}
                      className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-brand ring-2 ring-bg"
                    />
                  )}
                </span>
              </span>
            }
          </Link>
          );
        })}
      </div>
    </nav>
  );
}
